/**
 * HomeChatInput — the centered chat starter on the home screen.
 *
 * Distinct from ``ChatInput`` (which lives inside an active
 * conversation and additionally handles model picking, queue-while-busy,
 * dictation-setup modal, etc). On the home page most of those
 * affordances don't apply yet — there's no active conversation to
 * attach to, no queue, no follow-up history. This is the streamlined
 * "ask anything → create a new chat" launcher.
 *
 * Reuses four slices of the chat input's behaviour:
 *
 *   • **Slash commands & skills** via the shared catalog fetched from
 *     ``/api/slash-commands`` (built-ins + user / project skills +
 *     plugin commands). The palette pops above the input bar with the
 *     same keyboard model as ChatInput so muscle memory carries over.
 *   • **Dictation** via the shared ``useDictation`` hook. Same
 *     backend, same level meter, same Moonshine recognizer. If the
 *     model isn't installed yet we link the user to the in-chat
 *     setup modal rather than re-implementing it here.
 *   • **File attachment** via three entry points: a paperclip
 *     button (opens the native file dialog through ``/api/files/pick``,
 *     falls back to a hidden ``<input type="file">``), drag-and-drop
 *     onto the wrapper, and clipboard paste of images.
 *   • **Submission**: attachments upload, then their paths are appended
 *     to the outgoing message text and the chat store's
 *     ``attachedFiles`` staging buffer is cleared.
 *
 * On submit the order is ``createConversation`` → **upload attachments into
 * it** → ``openConversation`` → ``sendMessage``, so auto-title + WS streaming
 * behave identically to the in-chat path.
 *
 * That ordering is the whole point, and differs from ChatInput. Inside a chat
 * the target conversation already exists, so ChatInput can upload the moment a
 * file is attached. Here it does not exist yet — so attaching cannot upload,
 * because the only conversation id available at that moment is
 * ``activeConversationId``, i.e. the chat the user was last looking at. Home
 * used to do exactly that, which put attachments in the previous conversation's
 * workspace and, via the read-only admin view, in other tenants' workspaces
 * (2026-07-29). Files are therefore STAGED (``AttachedFile.file``) until submit.
 *
 * Slash commands take the same route — the new conversation receives
 * ``/<name>`` as its first message and the backend dispatches it like any
 * other turn.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchAudioStatus } from "@/api/audio";
import { authedUpload, nativeDialogAvailable, probeNativeDialog } from "@/api/client";
import { baseName, importPickedPaths } from "@/api/files";
import { useChatStore, type AttachedFile } from "@/stores/chatStore";
import { useDictation, DICTATION_LEVEL_BARS } from "@/hooks/useDictation";
import { ModelSelector } from "@/components/chat/ModelSelector";
import { ComposerPlusMenu } from "@/components/chat/ComposerPlusMenu";
import { SlashCommandMenu } from "@/components/chat/SlashCommandMenu";
import {
  filterSlashCommands,
  getCachedSlashCommands,
  getSlashCommands,
  type SlashCommand,
} from "@/components/chat/slashCommands";


export function HomeChatInput() {
  const createConversation = useChatStore((s) => s.createConversation);
  const openConversation = useChatStore((s) => s.openConversation);
  const resetModelForNewConversation = useChatStore((s) => s.resetModelForNewConversation);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const attachedFiles = useChatStore((s) => s.attachedFiles);
  const addFiles = useChatStore((s) => s.addFiles);
  const removeFile = useChatStore((s) => s.removeFile);
  const clearFiles = useChatStore((s) => s.clearFiles);

  // Draft text lives in the store, not local state, so it survives the
  // remount that happens when a background widget refresh flips HomeView
  // between its empty (centered) and populated (bottom-pinned) layouts —
  // those render the composer in different tree positions, which would
  // otherwise wipe mid-typing. ``setValue`` keeps the ``useState`` API
  // (direct value or functional updater) the rest of this file relies on.
  const value = useChatStore((s) => s.homeDraft);
  const setHomeDraft = useChatStore((s) => s.setHomeDraft);
  const setValue = useCallback(
    (next: string | ((prev: string) => string)) => {
      setHomeDraft(
        typeof next === "function"
          ? next(useChatStore.getState().homeDraft)
          : next,
      );
    },
    [setHomeDraft],
  );
  const [submitting, setSubmitting] = useState(false);
  /** Surfaced under the composer when an attachment upload fails at submit.
   *  The staged files are KEPT so the user can just press send again. */
  const [attachError, setAttachError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Slash-command palette ───────────────────────────────────────
  //
  // Same model as ChatInput: the palette opens whenever the message
  // begins with "/" and there's no whitespace yet (the moment the
  // user types an argument we close it). Catalog is fetched once per
  // page load via ``getSlashCommands`` and shared with ChatInput's
  // module-level cache, so the round-trip happens at most once even
  // if the user navigates between home and chat.
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const [catalog, setCatalog] = useState<SlashCommand[]>(() =>
    getCachedSlashCommands(),
  );
  const [catalogLoading, setCatalogLoading] = useState(catalog.length === 0);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // The Home composer is the entry point for a brand-new conversation, so
  // its ModelSelector must always start fresh: Auto, and unlocked. The
  // model + context view are global store state that the previous chat
  // leaves populated, and "New Conversation" only navigates here (the chat
  // isn't created until the first message is sent) — so without this the
  // picker would show the previous chat's model and, if that chat had a
  // large cached history (≥ MODEL_SWITCH_LOCK_TOKENS), stay locked.
  useEffect(() => {
    resetModelForNewConversation();
  }, [resetModelForNewConversation]);

  // Learn whether this box has an OS file dialog BEFORE the first attach click,
  // so ``handlePickFiles`` can stay synchronous where there's none (ChromeOS).
  useEffect(() => {
    probeNativeDialog();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSlashCommands()
      .then((cmds) => {
        if (cancelled) return;
        setCatalog(cmds);
        setCatalogError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCatalogError(err instanceof Error ? err.message : "fetch failed");
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const slashQuery = useMemo(() => {
    if (!value.startsWith("/")) return null;
    const firstSpace = value.search(/\s/);
    if (firstSpace !== -1) return null;
    return value.slice(1);
  }, [value]);
  const filteredCommands = useMemo<SlashCommand[]>(
    () =>
      slashQuery === null ? [] : filterSlashCommands(slashQuery, catalog),
    [slashQuery, catalog],
  );

  useEffect(() => {
    if (slashQuery === null) {
      setSlashOpen(false);
      return;
    }
    setSlashOpen(true);
    setSlashIndex(0);
  }, [slashQuery]);

  // ─── Dictation ───────────────────────────────────────────────────
  //
  // Mirrors ChatInput's setup: persist the chosen mic device id in
  // localStorage so the user only picks once per browser. If the
  // backend recognizer isn't installed yet the mic button is shown
  // in a "needs setup" state and clicking opens the in-chat setup
  // flow (the home page deliberately doesn't carry the modal itself
  // — it's a one-time thing best handled inside a chat session).
  const [micDeviceId] = useState<string | null>(() =>
    typeof window !== "undefined"
      ? localStorage.getItem("dictation.deviceId")
      : null,
  );
  const [modelReady, setModelReady] = useState<boolean | null>(null);

  const dictation = useDictation({
    onFinal: (segment) => {
      setValue((prev) => {
        if (!segment) return prev;
        const sep = prev.length === 0 || /\s$/.test(prev) ? "" : " ";
        return prev + sep + segment;
      });
    },
    deviceId: micDeviceId ?? undefined,
  });
  const isDictating =
    dictation.status === "recording" ||
    dictation.status === "requesting" ||
    dictation.status === "finalizing";

  useEffect(() => {
    let cancelled = false;
    fetchAudioStatus()
      .then((s) => {
        if (!cancelled) setModelReady(s.asr.installed);
      })
      .catch(() => {
        if (!cancelled) setModelReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMicClick = useCallback(() => {
    if (isDictating) {
      if (dictation.status === "recording") dictation.stop();
      return;
    }
    // First-time dictation setup (downloading the Moonshine model
    // + picking a mic device) lives in the chat input's modal. We
    // don't replicate it on the home page; users hit dictation
    // there once and home dictation works from then on. The button
    // is rendered disabled when ``modelReady === false`` so this
    // branch is just a safety net.
    if (modelReady === false) return;
    void dictation.start();
  }, [isDictating, dictation, modelReady]);

  // ─── File attachment ─────────────────────────────────────────────
  //
  // Three paths, same destination: chatStore.attachedFiles (a staging
  // buffer that flushes on send). The submit handler appends each
  // file's real filesystem path to the outgoing message text — same
  // as ChatInput does.

  /**
   * STAGE files — deliberately no upload here.
   *
   * The Home composer has no conversation of its own until submit, so there is
   * no correct ``conversation_id`` to upload against at attach time. It used to
   * fall back to ``activeConversationId`` — the chat the user last had open —
   * which meant a file attached on /home landed in the PREVIOUS conversation's
   * workspace, and, if that chat was someone else's opened through the
   * read-only admin view, in ANOTHER TENANT's workspace (2026-07-29). The turn
   * then started in a brand-new chat whose sandbox seeds only its own
   * workspace, so the agent could never see the file.
   *
   * The upload now happens in ``handleSubmit``, after the conversation exists.
   * A server-side re-home (``cowork.ui.uploads``) still backstops older
   * clients, but it cannot rescue a cross-tenant drop — it refuses to move
   * those, correctly — so the ordering has to be right here.
   */
  const stageFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      addFiles(files.map((f) => ({ name: f.name, path: "", file: f })));
      setAttachError(null);
    },
    [addFiles],
  );

  /**
   * Upload every staged file into ``conversationId`` and return the list with
   * real paths filled in. Entries that already carry a path (the native OS
   * picker hands us real filesystem paths) pass through untouched.
   *
   * Throws on the first failed upload: silently dropping a file here is how a
   * user ends up watching the agent answer without the document they attached.
   */
  const uploadStagedFiles = useCallback(
    async (files: AttachedFile[], conversationId: string): Promise<AttachedFile[]> => {
      const out: AttachedFile[] = [];
      // Natively-picked files arrive as a PATH with no File object. Passing
      // those through (the pre-2026-07-29 behaviour) attached
      // ``/home/me/Downloads/x.csv`` verbatim — which opens on this machine and
      // nowhere else, because file sync only replicates the conversation
      // workspace. Copy them in first, exactly like a browser upload; the
      // conversation already exists here, so they land in the right one.
      const picked = files.filter((f) => !f.file && f.path);
      let importedPaths: Record<string, string> = {};
      if (picked.length > 0) {
        const res = await importPickedPaths(
          picked.map((f) => f.path!), conversationId,
        );
        importedPaths = res.imported;
        const failures = Object.entries(res.failed);
        if (failures.length > 0) {
          // Same contract as a failed upload below: refuse loudly rather than
          // send a message pointing at a file the agent can't read.
          const [src, reason] = failures[0]!;
          throw new Error(`Couldn't attach ${baseName(src)} — ${reason}`);
        }
      }
      for (const f of files) {
        if (!f.file) {
          if (f.path) out.push({ ...f, path: importedPaths[f.path] ?? f.path });
          continue;
        }
        const form = new FormData();
        form.append("file", f.file, f.name || "upload");
        const res = await authedUpload(
          `/api/files/drop?conversation_id=${encodeURIComponent(conversationId)}`,
          form,
        );
        if (!res.ok) {
          throw new Error(`Couldn't upload ${f.name} (${res.status})`);
        }
        const data = await res.json();
        if (!data?.path) throw new Error(`Couldn't upload ${f.name}`);
        out.push({ name: f.name, path: data.path });
      }
      return out;
    },
    [],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;
      stageFiles(Array.from(fileList));
      e.target.value = "";
    },
    [stageFiles],
  );

  const handlePickFiles = useCallback(() => {
    // Only invoke the server-side OS dialog when we've CONFIRMED one exists
    // (Linux desktop / macOS / Windows). Otherwise — web, or ChromeOS Crostini
    // which has no zenity — click the hidden <input> SYNCHRONOUSLY inside the
    // user gesture. Awaiting /api/files/pick first and clicking only after the
    // round-trip forfeits WebKit's user-activation, so the picker returns an
    // empty selection and no attachment appears (2026-07-21). The availability
    // is probed at mount (below), so this branch stays synchronous.
    if (nativeDialogAvailable() === true) {
      void (async () => {
        try {
          const res = await fetch("/api/files/pick");
          const data = res.ok ? await res.json() : null;
          if (data?.paths?.length) {
            // Native picker only — these are already real filesystem paths on a
            // box that shares its FS with the agent, so there is nothing to
            // upload and no conversation to scope them to.
            addFiles(
              data.paths.map((p: string) => ({
                name: p.split("/").pop() || p.split("\\").pop() || p,
                path: p,
              })),
            );
            setAttachError(null);
          } else if (data?.fallback) {
            // Availability changed under us — last-resort browser picker.
            fileInputRef.current?.click();
          }
        } catch {
          fileInputRef.current?.click();
        }
      })();
      return;
    }
    fileInputRef.current?.click();
  }, [addFiles]);

  // Clipboard paste: scoop image files out of the clipboard and
  // upload them through the same /api/files/drop flow. Plain text
  // paste passes through unchanged.
  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const images: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind !== "file") continue;
        if (!item.type.startsWith("image/")) continue;
        const file = item.getAsFile();
        if (!file) continue;
        const ext = (item.type.split("/")[1] || "png").split("+")[0];
        const stamp = new Date()
          .toISOString()
          .replace(/[:.]/g, "-")
          .replace("T", "_")
          .replace(/Z$/, "");
        const name = `pasted-image-${stamp}.${ext}`;
        images.push(new File([file], name, { type: item.type }));
      }
      if (images.length === 0) return;
      e.preventDefault();
      stageFiles(images);
    },
    [stageFiles],
  );

  // Drag-and-drop on the form wrapper. ``dragCounter`` tracks nested
  // enter/leave events so the overlay doesn't flicker when the cursor
  // crosses an inner element.
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      if (e.dataTransfer.files.length === 0) return;
      stageFiles(Array.from(e.dataTransfer.files));
    },
    [stageFiles],
  );

  // ─── Textarea sizing ─────────────────────────────────────────────
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [value]);

  // ─── Submit ──────────────────────────────────────────────────────
  const trimmed = value.trim();
  const canSubmit = (trimmed.length > 0 || attachedFiles.length > 0) && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setAttachError(null);
    try {
      // ORDER IS LOAD-BEARING: create → upload → send.
      //
      // The conversation must exist before its attachments upload, so every
      // file lands in the workspace the turn will actually seed. Uploading
      // first (the pre-2026-07-29 order) has to guess a target, and the guess
      // was ``activeConversationId`` — the previously open chat, possibly
      // another user's when reached from the read-only admin view.
      const id = await createConversation();
      const resolved = await uploadStagedFiles(attachedFiles, id);

      let message = trimmed;
      if (resolved.length > 0) {
        const fileSection =
          resolved.length === 1
            ? `\n\nAttached file: ${resolved[0]!.path}`
            : `\n\nAttached files:\n${resolved.map((f) => `- ${f.path}`).join("\n")}`;
        message = (message || "Please analyze the attached files") + fileSection;
      }
      if (!message) return;

      // Only now does the agent start — with paths that resolve inside its own
      // sandbox.
      await openConversation(id);
      sendMessage(message);
      setValue("");
      clearFiles();
    } catch (err) {
      console.error("HomeChatInput: failed to start conversation:", err);
      // Keep the staged files: the user can press send again without
      // re-attaching. A failed upload must never look like a sent message.
      setAttachError(
        err instanceof Error ? err.message : "Couldn't start the conversation",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit, trimmed, attachedFiles, setValue, uploadStagedFiles,
    createConversation, openConversation, sendMessage, clearFiles,
  ]);

  // Insert a slash command into the textarea, optionally firing the
  // submit handler for zero-arg commands. Same UX contract as
  // ChatInput.applySlashCommand so muscle memory transfers.
  const applySlashCommand = useCallback(
    (cmd: SlashCommand, andSubmit: boolean) => {
      const wantsArg = !!cmd.args;
      const inserted = wantsArg ? `/${cmd.name} ` : `/${cmd.name}`;
      setValue(inserted);
      setSlashOpen(false);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(inserted.length, inserted.length);
      });
      if (andSubmit && !wantsArg) {
        // The submit handler captures ``value`` via closure — we just
        // re-implement the minimal slash-only flow inline to avoid a
        // stale-closure race against the setValue we just issued.
        void (async () => {
          setSubmitting(true);
          try {
            const id = await createConversation();
            await openConversation(id);
            sendMessage(inserted);
            setValue("");
            clearFiles();
          } catch (err) {
            console.error("HomeChatInput: slash submit failed:", err);
          } finally {
            setSubmitting(false);
          }
        })();
      }
    },
    [createConversation, openConversation, sendMessage, clearFiles, setValue],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Slash palette takes precedence — same key map as ChatInput.
      if (slashOpen && filteredCommands.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSlashIndex((i) => (i + 1) % filteredCommands.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSlashIndex(
            (i) =>
              (i - 1 + filteredCommands.length) % filteredCommands.length,
          );
          return;
        }
        if (e.key === "Tab") {
          e.preventDefault();
          const cmd = filteredCommands[slashIndex];
          if (cmd) applySlashCommand(cmd, false);
          return;
        }
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const cmd = filteredCommands[slashIndex];
          if (cmd) applySlashCommand(cmd, true);
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setSlashOpen(false);
          return;
        }
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void handleSubmit();
      }
    },
    [handleSubmit, slashOpen, filteredCommands, slashIndex, applySlashCommand],
  );

  // ─── Render ──────────────────────────────────────────────────────

  const form = (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="relative"
    >
      {/* Drop overlay — only paints during a drag with files. */}
      {isDragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center
                        rounded-2xl bg-jove-10/90 border-2 border-dashed border-jove
                        pointer-events-none animate-[fadeInUp_120ms_ease-out]">
          <div className="flex items-center gap-2 text-jove text-sm font-semibold">
            <svg className="w-5 h-5 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Drop to attach
          </div>
        </div>
      )}

      {/* Attached file chips — shown above the input bar. */}
      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {attachedFiles.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-jove-10 border border-jove-60/40 rounded-lg text-xs animate-[scaleIn_150ms_ease-out]"
            >
              <svg className="h-3.5 w-3.5 text-jove-80 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              {/* ``code-ink``, not the fixed ``jove-200``: the 60/80/200/400 ink
                  shades don't re-theme, so #3610A2 on the dark ``bg-jove-10``
                  tint was near-black on near-black. Same token inline `code`
                  uses — jove-200 in light, light lavender in dark. Mirrors the
                  chat composer's chip. */}
              <span
                className="text-code-ink font-medium truncate max-w-[200px]"
                title={file.path || file.name}
              >
                {file.name}
              </span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                aria-label={`Remove ${file.name}`}
                className="text-space-40 hover:text-hydra transition-colors ml-0.5"
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Slash-command palette anchor. The menu uses ``absolute
          bottom-full`` so it floats above the input card. The outer
          ``relative`` wrapper around this whole form supplies the
          positioning context. */}
      {slashOpen && (
        <SlashCommandMenu
          commands={filteredCommands}
          activeIndex={slashIndex}
          onSelect={(cmd) => applySlashCommand(cmd, false)}
          onHoverIndex={setSlashIndex}
          query={slashQuery ?? ""}
          loading={catalogLoading}
          error={catalogError}
        />
      )}

      <div
        className={`relative rounded-2xl border bg-surface shadow-lg
                    transition-all duration-200
                    ${trimmed || attachedFiles.length
                      ? "border-jove-60/60 shadow-jove-60/10"
                      : "border-space-30 hover:border-jove-60/30"}`}
      >
        {/* Main input row */}
        <div className="flex items-end gap-1.5 px-3 py-2">
        {/* Hidden file input — fallback for ChromeOS where the native
            picker dialog isn't available. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* "+" menu — add files + per-conversation connector toggles. On the
            home composer the picks are NOT persisted to a conversation (there
            isn't one yet); they ride along on createConversation. */}
        <ComposerPlusMenu onAddFiles={handlePickFiles} persistConnectors={false} />

        {/* Textarea, OR the dictation level meter while recording. */}
        {isDictating ? (
          <DictationOverlay
            levels={dictation.levels}
            status={dictation.status}
            onStop={() => dictation.stop()}
            onCancel={() => dictation.cancel()}
          />
        ) : (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={submitting}
            // Spellchecker underlines on ``/loop`` etc. are noisy;
            // disable it whenever the message looks slash-like.
            spellCheck={!value.startsWith("/")}
            rows={1}
            placeholder="Ask anything to get started…"
            className="flex-1 resize-none bg-transparent text-space placeholder:text-space-40
                       focus:outline-none max-h-[200px] py-1.5 leading-relaxed
                       disabled:opacity-60 text-base"
          />
        )}

        {/* Mic button. Visually distinct treatments for the four
            states: idle, recording, finalizing, "needs setup". */}
        <button
          type="button"
          onClick={handleMicClick}
          disabled={
            submitting
            || dictation.status === "finalizing"
            || modelReady === false
          }
          aria-label={isDictating ? "Stop dictation" : "Start dictation"}
          title={
            dictation.status === "recording"
              ? "Stop and transcribe"
              : dictation.status === "finalizing"
                ? "Transcribing…"
                : modelReady === false
                  ? "Open a chat and dictate there once to install the recognizer"
                  : "Dictate"
          }
          className={`p-2 rounded-xl transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed
                      ${
                        dictation.status === "recording"
                          ? "bg-hydra-50 text-hydra hover:bg-hydra hover:text-white"
                          : dictation.status === "finalizing"
                            ? "bg-amber-50 text-amber-600"
                            : "text-space-40 hover:text-jove hover:bg-jove-10"
                      }`}
        >
          {dictation.status === "finalizing" ? (
            <svg className="w-4 h-4 bento-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0m7 7v3m-4 0h8m-4-9a3 3 0 01-3-3V5a3 3 0 116 0v4a3 3 0 01-3 3z" />
            </svg>
          )}
        </button>

        {/* Send — primary action. */}
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          aria-label="Start conversation"
          className={`p-2 rounded-xl transition-all duration-150 shrink-0 self-end
                      ${canSubmit
                        ? "bg-primary text-on-primary hover:bg-jove-200 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                        : "bg-space-20 text-space-40 cursor-not-allowed"}`}
        >
          {submitting ? (
            <svg className="w-4 h-4 bento-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m0 0l-6-6m6 6l-6 6" />
            </svg>
          )}
        </button>
        </div>{/* end main input row */}

        {/* Model selector footer — mirrors ChatInput's footer bar.
            ``lockWhileRunning={false}``: ``isAgentRunning`` reflects the chat
            the user just left, which may still be running in the background.
            That must not disable the picker here — this composer builds a brand
            new chat with no turn of its own in flight, and choosing its model is
            the whole point. The switch lock never fires here either: HomeView
            resets the active-conversation context on mount, so there is no stale
            cached-history count to lock against for a brand-new chat. */}
        <div className="flex items-center px-3 pb-2 pt-1 border-t border-space-20">
          <ModelSelector lockWhileRunning={false} persistSelection={false} />
        </div>
      </div>

      {/* Attachment / start failure. Shown instead of silently swallowing the
          error, because the staged files are still here and a retry is one
          click away. */}
      {attachError && (
        <p className="text-xs text-hydra mt-1 px-1" role="alert">
          {attachError}
        </p>
      )}

      {/* Dictation error — only shows when present, doesn't take
          baseline layout space. */}
      {dictation.error && !isDictating && (
        <p className="text-[10px] text-hydra mt-1 px-1 max-w-[260px] truncate" title={dictation.error}>
          {dictation.error}
        </p>
      )}
    </div>
  );

  return <div className="w-full max-w-2xl mx-auto">{form}</div>;
}


/** Inline level meter shown in place of the textarea while the user
 *  is dictating. Stop and Cancel are inline so a user can finish or
 *  drop the recording without leaving the input row. */
function DictationOverlay({
  levels,
  status,
  onStop,
  onCancel,
}: {
  levels: number[];
  status: string;
  onStop: () => void;
  onCancel: () => void;
}) {
  // Pad the levels array to a fixed width so the meter doesn't
  // re-flow as samples arrive.
  const padded = [...levels];
  while (padded.length < DICTATION_LEVEL_BARS) padded.push(0);

  return (
    <div className="flex-1 flex items-center gap-2 py-1.5">
      <div className="flex-1 flex items-center gap-[2px] h-6">
        {padded.slice(0, DICTATION_LEVEL_BARS).map((level, i) => {
          const heightPct = Math.max(8, Math.min(100, level * 100));
          return (
            <span
              key={i}
              className="flex-1 bg-jove rounded-full transition-[height] duration-75"
              style={{ height: `${heightPct}%` }}
            />
          );
        })}
      </div>
      <span className="text-[10px] text-jove font-medium uppercase tracking-wider min-w-[60px] text-right">
        {status === "finalizing" ? "Decoding…" : "Listening"}
      </span>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancel dictation"
        className="p-1 rounded-md text-space-40 hover:text-hydra hover:bg-hydra-50 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      {/* When recording, the mic-button outside the overlay handles
          stop. When the API surfaces a separate explicit stop, expose
          it inline so users with assistive tech can find it. */}
      {status === "recording" && (
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop and transcribe"
          className="p-1 rounded-md text-jove hover:bg-jove-10 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </button>
      )}
    </div>
  );
}
